import { Link, useNavigate } from '@tanstack/react-router';
import { Gavel } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { DECISION_RULE_LABELS, OVERRIDE_KIND_LABELS, STAGE_DECISION_OUTCOME_LABELS, type DecisionLogKind, type DecisionLogRow, type JobDetail } from '@nest-recruit/shared';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import { DECISION_OUTCOME_TONES } from '../../../components/status-tones';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { SkeletonLines } from '../../../components/Skeleton';
import { ToneChip, type Tone } from '../../../components/Tone';
import { ForbiddenState, ServerErrorState, SystemState } from '../../../components/system-states/SystemState';
import { EMPTY, formatDateTime } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useDecisionLog } from '../api/decisions-api';
import styles from './DecisionLogTab.module.css';

/** Lines with no round outcome: waivers (S20) and the comparison's decisions (S22). A waiver reads like a waived scorecard. */
const KIND_CHIPS: Readonly<Record<DecisionLogKind, { readonly tone: Tone; readonly label: string }>> = {
  decision: { tone: 'done', label: 'Decision' },
  waiver: { tone: 'ended', label: 'Waiver' },
  final_selection: { tone: 'done', label: 'Final selection' },
  eliminated: { tone: 'rejected', label: 'Eliminated' },
};

/**
 * DEC-03 — every decision made on this job: who, what, when, why (E10). Waivers sit
 * beside the decisions they cleared the way for, and every override shows its reason —
 * 06-qol §12: "every override, waiver and skip is visible in the record's history".
 * Where N-45 lands.
 */
export function DecisionLogTab({ detail }: { readonly detail: JobDetail }): ReactNode {
  const query = useDecisionLog(detail.id, true);
  const navigate = useNavigate();
  const [sort, setSort] = useState<readonly SortSpec[]>([{ columnId: 'at', direction: 'desc' }]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const rows = useMemo(() => sorted(query.data?.rows ?? [], sort), [query.data, sort]);

  if (query.isPending) return <SkeletonLines count={6} />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this job's decisions" contact="Your admin" />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  if (rows.length === 0) {
    return <SystemState icon={Gavel} title="No round decided yet" body="Each round is decided on its debrief, once every scorecard is in. Decisions, tie-breaks, overrides, waivers and the final selection on this job collect here." isInset />;
  }

  const columns: readonly Column<DecisionLogRow>[] = [
    { id: 'at', header: 'When', isSortable: true, defaultWidth: 170, cell: (row) => formatDateTime(row.at) },
    { id: 'candidate', header: 'Candidate', isSortable: true, isIdentity: true, defaultWidth: 180, cell: (row) => <CandidateLink row={row} jobId={detail.id} />, exportValue: (row) => row.candidateName },
    { id: 'round', header: 'Round', defaultWidth: 130, cell: (row) => row.roundName },
    { id: 'what', header: 'What', isSortable: true, defaultWidth: 130, cell: (row) => <What row={row} /> },
    { id: 'by', header: 'By', isSortable: true, defaultWidth: 150, cell: (row) => row.actorName },
    {
      id: 'rule',
      header: 'Rule and score',
      defaultWidth: 140,
      cell: (row) => (row.rule ? `${DECISION_RULE_LABELS[row.rule]} · ${row.roundScore === null ? EMPTY : row.roundScore.toFixed(2)}` : null),
    },
    { id: 'why', header: 'Why', defaultWidth: 320, cell: (row) => <Why row={row} /> },
    { id: 'took', header: 'Took', isNumeric: true, defaultWidth: 80, cell: (row) => (row.latencyMinutes === null ? null : tookWords(row.latencyMinutes)) },
  ];

  return (
    <DataTable
      screenId="job-decision-log"
      label="Decisions on this job"
      columns={columns}
      rows={rows}
      toneOf={(row) => (row.outcome ? DECISION_OUTCOME_TONES[row.outcome] : undefined)}
      getRowId={(row) => `${row.kind}:${row.id}`}
      total={rows.length}
      sort={sort}
      onSortChange={setSort}
      selection={selection}
      onSelectionChange={setSelection}
      isSelectable={false}
      onOpenRow={(row) =>
        void (row.interviewId
          ? navigate({ to: '/interviews/$interviewId/debrief', params: { interviewId: row.interviewId } })
          : row.kind === 'final_selection' || row.kind === 'eliminated'
            ? navigate({ to: '/jobs/$jobId/$tab', params: { jobId: detail.id, tab: 'compare' } })
            : navigate({ to: '/applications/$applicationId', params: { applicationId: row.applicationId } }))
      }
    />
  );
}

/** The row's one link (DECISIONS-LOG 2026-10-06), to where the decision was made. Enter on the row goes to the same place. */
function CandidateLink({ row, jobId }: { readonly row: DecisionLogRow; readonly jobId: string }): ReactNode {
  if (row.interviewId) {
    return (
      <Link to="/interviews/$interviewId/debrief" params={{ interviewId: row.interviewId }} className={styles.candidate}>
        {row.candidateName}
      </Link>
    );
  }
  if (row.kind === 'final_selection' || row.kind === 'eliminated') {
    return (
      <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'compare' }} className={styles.candidate}>
        {row.candidateName}
      </Link>
    );
  }
  return (
    <Link to="/applications/$applicationId" params={{ applicationId: row.applicationId }} className={styles.candidate}>
      {row.candidateName}
    </Link>
  );
}

function What({ row }: { readonly row: DecisionLogRow }): ReactNode {
  return (
    <span className={styles.chips}>
      {row.outcome ? (
        <ToneChip tone={DECISION_OUTCOME_TONES[row.outcome]}>
          {STAGE_DECISION_OUTCOME_LABELS[row.outcome]}
        </ToneChip>
      ) : (
        <ToneChip tone={KIND_CHIPS[row.kind].tone}>
          {KIND_CHIPS[row.kind].label}
        </ToneChip>
      )}
      {row.wasTieBroken && (
        <Chip tone="attention" emphasis="solid">
          Tie broken
        </Chip>
      )}
      {/* 06-qol §12: every override visible — never a quiet grey label. */}
      {row.wasOverridden && (
        <Chip tone="warning" emphasis="solid">
          Override
        </Chip>
      )}
    </span>
  );
}

function Why({ row }: { readonly row: DecisionLogRow }): ReactNode {
  const lines = [
    row.reason,
    row.wasOverridden ? `Against ${row.overrideKinds.map((kind) => OVERRIDE_KIND_LABELS[kind]).join(' and ')}: "${row.overrideReason ?? EMPTY}"` : null,
    row.note,
  ].filter((line): line is string => line !== null && line !== '');
  if (lines.length === 0) return null;
  return (
    <span className={styles.why}>
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </span>
  );
}

function tookWords(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} days`;
}

const SORT_VALUES: Readonly<Record<string, (row: DecisionLogRow) => string>> = {
  at: (row) => row.at,
  candidate: (row) => row.candidateName.toLowerCase(),
  what: (row) => row.outcome ?? row.kind,
  by: (row) => (row.actorName ?? '').toLowerCase(),
};

function sorted(rows: readonly DecisionLogRow[], sort: readonly SortSpec[]): readonly DecisionLogRow[] {
  if (sort.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const spec of sort) {
      const value = SORT_VALUES[spec.columnId];
      if (!value) continue;
      const order = value(a).localeCompare(value(b));
      if (order !== 0) return spec.direction === 'asc' ? order : -order;
    }
    return 0;
  });
}
